import { useEffect, useMemo, useState } from 'react'
import { CheckIcon, PrinterIcon, XIcon } from '../icons'
import { formatClock } from '../../utils/dayKey'
import { getPrintHistory, PRINT_HISTORY_EVENT, slipRows, type SlipRow } from '../../utils/printHistory'
import { DEFAULT_STATION, type Order } from '../../types'
import type { ReceiptKind } from '../receipt/Receipt'
import './RecentSlipsDialog.css'

/**
 * Son fişler (24 Eki 2026). Rulo bitince yazıcı son fişleri basmıyor ya da
 * yarım basıyor; kasiyer kağıdı takıp buradan tekrar basıyor. Tezgah fişleri
 * tezgah tezgah: yalnız kağıdı biten yazıcının fişi basılsın, öbür tezgahlar
 * aynı siparişi ikinci kez almasın. Tekrar basılan fişin başında "TEKRAR
 * BASIM" yazıyor.
 */

function label(o: Order): string {
  if (o.tableName) return o.tableName
  if (o.takeawayNo) return `Gel-Al #${o.takeawayNo}`
  if (o.receiptNo) return `Fiş ${o.receiptNo}`
  return 'Gel-Al'
}

function items(o: Order): string {
  const shown = o.lines.slice(0, 4).map((l) => `${l.qty}× ${l.name}`)
  const more = o.lines.length - shown.length
  return more > 0 ? `${shown.join(', ')} ve ${more} ürün daha` : shown.join(', ')
}

/** Üst çubuktaki düğmenin attığı olay; pencereyi App açıyor (print orada). */
export const RECENT_SLIPS_EVENT = 'adissoo:recent-slips'

const KIND_TAG: Record<ReceiptKind, string> = { station: 'Sipariş', cash: 'Ödeme', move: 'Masa değişti' }

interface Props {
  onPrint: (order: Order, kind: ReceiptKind) => void
  onClose: () => void
}

export function RecentSlipsDialog({ onPrint, onClose }: Props) {
  const [history, setHistory] = useState(getPrintHistory)
  /** Bu pencerede tekrar basılanlar: düğme "Basıldı" oluyor. */
  const [done, setDone] = useState<Set<string>>(new Set())

  useEffect(() => {
    const on = () => setHistory(getPrintHistory())
    window.addEventListener(PRINT_HISTORY_EVENT, on)
    return () => window.removeEventListener(PRINT_HISTORY_EVENT, on)
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const rows = useMemo(() => slipRows(history, DEFAULT_STATION), [history])

  const reprint = (r: SlipRow) => {
    onPrint({ ...r.order, reprint: true }, r.kind)
    setDone((prev) => new Set(prev).add(r.key))
  }

  return (
    <div className="dlg" role="dialog" aria-modal="true" aria-label="Son fişler" onClick={onClose}>
      <div className="dlg__card recent" onClick={(e) => e.stopPropagation()}>
        <div className="recent__head">
          <h2>Son fişler</h2>
          <button type="button" className="recent__x" onClick={onClose} aria-label="Kapat">
            <XIcon />
          </button>
        </div>
        <p className="recent__lead">
          Kağıt bittiyse ya da fiş eksik çıktıysa, kağıdı takıp eksik fişi buradan tekrar basın.
          Fişin başında "TEKRAR BASIM" yazar.
        </p>

        {rows.length === 0 ? (
          <p className="recent__empty">Bu bilgisayardan henüz fiş basılmadı.</p>
        ) : (
          <ul className="recent__list">
            {rows.map((r) => (
              <li key={r.key} className="recent__row">
                <span className="recent__time">{formatClock(r.at)}</span>
                <div className="recent__info">
                  <div className="recent__title">
                    <b>{label(r.order)}</b>
                    <span className={`recent__tag is-${r.kind}`}>
                      {r.kind === 'cash' ? KIND_TAG.cash : `${r.part}, ${KIND_TAG[r.kind]}`}
                    </span>
                  </div>
                  <span className="recent__items">{items(r.order)}</span>
                </div>
                <button
                  type="button"
                  className={`btn ${done.has(r.key) ? 'btn--outline' : 'btn--primary'} recent__print`}
                  onClick={() => reprint(r)}
                >
                  {done.has(r.key) ? <CheckIcon /> : <PrinterIcon />}
                  {done.has(r.key) ? 'Basıldı' : 'Bas'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
